import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, X, ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { createCleaningTask, updateCleaningTask } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ShareWithPicker } from '@/components/ShareWithPicker';

export const CleaningDialog = ({ 
  open, 
  onClose, 
  selectedDate, 
  properties, 
  selectedProperty,
  editingTask = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    scheduled_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    notes: '',
    assigned_to: '',
    collaborator_ids: [],
    photos: [],
    hourly_rate: '',
  });
  const [date, setDate] = useState(selectedDate || null);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editingTask) {
      setFormData({
        property_id: editingTask.property_id || '',
        scheduled_date: editingTask.scheduled_date || '',
        notes: editingTask.notes || '',
        assigned_to: editingTask.assigned_to || '',
        collaborator_ids: editingTask.collaborator_ids || [],
        photos: editingTask.photos || [],
        hourly_rate: editingTask.hourly_rate || '',
      });
      if (editingTask.scheduled_date) {
        setDate(new Date(editingTask.scheduled_date + 'T00:00:00'));
      }
      return;
    }
    if (selectedProperty) {
      setFormData(prev => ({ ...prev, property_id: selectedProperty.id }));
    }
    if (selectedDate) {
      setDate(selectedDate);
      setFormData(prev => ({ ...prev, scheduled_date: format(selectedDate, 'yyyy-MM-dd') }));
    }
  }, [open, selectedProperty, selectedDate, editingTask]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.scheduled_date) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      if (editingTask) {
        await updateCleaningTask(editingTask.id, {
          notes: formData.notes,
          assigned_to: formData.assigned_to,
          collaborator_ids: formData.collaborator_ids,
          scheduled_date: formData.scheduled_date,
          hourly_rate: parseFloat(formData.hourly_rate) || 0,
        });
        toast.success('Cleaning task updated');
      } else {
        await createCleaningTask({
          ...formData,
          hourly_rate: parseFloat(formData.hourly_rate) || 0,
        });
        toast.success('Cleaning task created successfully');
      }
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cleaning task');
    } finally {
      setLoading(false);
    }
  };

  const addPhoto = () => {
    if (newPhotoUrl.trim()) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, newPhotoUrl.trim()]
      }));
      setNewPhotoUrl('');
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      property_id: selectedProperty?.id || '',
      scheduled_date: '',
      notes: '',
      assigned_to: '',
      collaborator_ids: [],
      photos: [],
      hourly_rate: '',
    });
    setDate(null);
    setNewPhotoUrl('');
  };

  return (
    <Dialog open={open} onOpenChange={() => { resetForm(); onClose(); }}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="cleaning-dialog">
        <DialogHeader>
          <DialogTitle className="font-manrope">{editingTask ? 'Edit Cleaning Task' : 'New Cleaning Task'}</DialogTitle>
          <DialogDescription className="text-slate-500">Schedule a cleaning task and assign a worker.</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Property Selection */}
          <div className="space-y-2">
            <Label>Property *</Label>
            <Select
              value={formData.property_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
            >
              <SelectTrigger data-testid="cleaning-property-select">
                <SelectValue placeholder="Select property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Scheduled Date */}
          <div className="space-y-2">
            <Label>Scheduled Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-slate-50",
                    !date && "text-muted-foreground"
                  )}
                  data-testid="cleaning-date-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => {
                    setDate(d);
                    setFormData(prev => ({ 
                      ...prev, 
                      scheduled_date: d ? format(d, 'yyyy-MM-dd') : '' 
                    }));
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Assigned To */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assigned_to">Assigned To</Label>
              <Input
                id="assigned_to"
                value={formData.assigned_to}
                onChange={(e) => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                placeholder="Cleaner name"
                className="bg-slate-50"
                data-testid="cleaning-assigned-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
              <Input
                id="hourly_rate"
                type="number"
                min="0"
                step="0.01"
                value={formData.hourly_rate}
                onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                placeholder="25.00"
                className="bg-slate-50"
                data-testid="cleaning-hourly-rate-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Special cleaning instructions..."
              className="bg-slate-50"
              rows={3}
              data-testid="cleaning-notes-input"
            />
          </div>

          {/* Share With (collaborators) */}
          <ShareWithPicker
            value={formData.collaborator_ids}
            onChange={(ids) => setFormData((prev) => ({ ...prev, collaborator_ids: ids }))}
            excludeIds={[formData.assigned_to]}
            testIdPrefix="cleaning-share-with"
          />

          {/* Photos */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Photos (optional)
            </Label>
            <div className="flex gap-2">
              <Input
                value={newPhotoUrl}
                onChange={(e) => setNewPhotoUrl(e.target.value)}
                placeholder="Paste image URL..."
                className="bg-slate-50 flex-1"
                data-testid="cleaning-photo-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="icon"
                onClick={addPhoto}
                disabled={!newPhotoUrl.trim()}
                data-testid="cleaning-add-photo-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Photo Preview Grid */}
            {formData.photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {formData.photos.map((photo, index) => (
                  <div key={photo || `photo-${index}`} className="relative group">
                    <img
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg border border-slate-200"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/100?text=Invalid+URL';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`cleaning-remove-photo-${index}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => { resetForm(); onClose(); }}
              data-testid="cleaning-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="cleaning-submit-btn"
            >
              {loading
                ? (editingTask ? 'Saving...' : 'Creating...')
                : (editingTask ? 'Save Changes' : 'Create Task')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CleaningDialog;
